import React, { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/AddOutlined";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import {
  NAV_TREE,
  navTarget,
  nodeUrl,
  nodeVisible,
  scopeReady,
} from "../../shared/navigationRegistry";
import { NavDrawerContext } from "../shared/NavDrawerButton";

// Renders the full app navigation tree (NAV_TREE) as an accordion inside the
// School left drawer, right after the school switcher. Top-level links with
// sub-links collapse/expand independently (multiple may be open at once);
// links whose scope is not satisfied render greyed-out and disabled, matching
// the DashboardMenu grid. Visibility and scope gating reuse the registry's
// nodeVisible / scopeReady helpers so this stays in lockstep with the modal.
// Trailing "+" that adds the row's destination to the dashboard bookmarks.
// Only rendered when an onAddBookmark callback is supplied (the Dashboard Menu
// passes one; the real sidebar/drawer does not, so it stays unchanged).
function AddBookmarkButton({ node, onAddBookmark, bookmarkedIds = [] }) {
  if (!onAddBookmark) return null;
  const added = bookmarkedIds.includes(node.id);
  return (
    <IconButton
      size="small"
      disabled={added}
      aria-label={
        added
          ? `${node.label} already bookmarked`
          : `Add ${node.label} bookmark`
      }
      onClick={(e) => {
        e.stopPropagation();
        onAddBookmark(node.id);
      }}
      sx={{ ml: 1 }}
    >
      {added ? (
        <BookmarkIcon sx={{ fontSize: 20, color: "#00000080" }} />
      ) : (
        <AddIcon sx={{ fontSize: 20, color: "primary.main" }} />
      )}
    </IconButton>
  );
}

// Fixed-width leading slot showing the expand/collapse state. It is a plain
// indicator now, not a button - the whole parent row is the expand trigger
// (groups carry no route, so clicking one only toggles its sub-links). For
// leaves (and child rows) it renders an empty box of the same width so every
// row's icon and label columns line up.
function ExpandSlot({ hasChildren, open }) {
  return (
    <Box
      sx={{
        width: 30,
        mr: 0.5,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {hasChildren &&
        (open ? (
          <ExpandLessIcon sx={{ fontSize: 22, color: "#00000080" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 22, color: "#00000080" }} />
        ))}
    </Box>
  );
}

// `boxed` wraps each top-level item (a leaf, or a parent together with its
// inline children) in its own glass container. The Dashboard launchpad turns
// this on; the drawer/sidebar leaves it off and renders a plain list.
export default function SchoolNavMenu({
  appProps,
  schoolSlug,
  schoolYearId = null,
  termId = null,
  navigationMode = "spa",
  onAddBookmark = null,
  bookmarkedIds = [],
  boxed = false,
}) {
  const theme = useTheme();
  const spaNavigate = useNavigate();
  const location = useLocation();
  const { close: closeDrawer } = useContext(NavDrawerContext);

  // Preselect a year/term the same way DashboardMenu does (URL param, then the
  // active school year, then the first available) so term-scoped links resolve
  // to a sensible default instead of staying greyed-out. The drawer has no
  // year/term selectors, so this is purely derived - never user-picked here.
  const years = appProps?.school_years ?? [];
  const yearId =
    schoolYearId ?? appProps?.school_year?.id ?? years[0]?.id ?? null;
  const yearObj = years.find((sy) => String(sy.id) === String(yearId));
  const resolvedTermId = termId ?? yearObj?.terms?.[0]?.id ?? null;

  const ctx = {
    schoolId: appProps?.school?.id ?? null,
    schoolSlug,
    school: appProps?.school ?? null,
    permissions: appProps?.permissions ?? {},
    yearId,
    termId: resolvedTermId,
  };

  const nodes = useMemo(
    () => NAV_TREE.filter((node) => nodeVisible(node, ctx)),
    [ctx.schoolId, ctx.schoolSlug, ctx.yearId, ctx.termId]
  );

  // Which link is active depends on the real browser path. In SPA mode
  // react-router's location is in sync with window.location; in "browser" mode
  // the navbar lives in a MemoryRouter (legacy v1 pages) whose location is
  // decoupled from the address bar, so read window.location directly. Match on
  // the path only - query params (school_year, term) are context, not identity.
  const currentPath =
    navigationMode === "browser" ? window.location.pathname : location.pathname;
  const isActive = (node) => nodeUrl(node, ctx)?.split("?")[0] === currentPath;

  // Default-open the section that holds the active link. Groups have no page of
  // their own, so a section opens only when one of its children is active.
  const initialExpanded = useMemo(() => {
    const state = {};
    nodes.forEach((node) => {
      if (!node.children) return;
      const childActive = node.children.some((child) => isActive(child));
      if (childActive) state[node.id] = true;
    });
    return state;
  }, [nodes, currentPath]);

  const [expanded, setExpanded] = useState(initialExpanded);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleNavigate = (node) => {
    const target = navTarget(node, ctx);
    if (!target || !scopeReady(target, ctx)) return;
    const url = target.resolve(ctx);
    if (navigationMode === "browser") {
      window.location.assign(url);
    } else if (!target.v1) {
      spaNavigate(url);
    } else {
      window.location.href = url;
    }
    closeDrawer?.();
  };

  return (
    <Box sx={{ pb: 3 }}>
      <List
        sx={{
          p: 0,
          ...(boxed && { display: "flex", flexDirection: "column", gap: 1 }),
        }}
      >
        {nodes.map((node) => {
          const Icon = node.icon;
          const children = (node.children ?? []).filter((child) =>
            nodeVisible(child, ctx)
          );
          const hasChildren = children.length > 0;
          const ready = scopeReady(node, ctx);
          const open = Boolean(expanded[node.id]);
          // Groups never highlight as the current page (they have no route);
          // only leaf rows do.
          const active = hasChildren ? false : isActive(node);

          return (
            <Box
              key={node.id}
              sx={
                boxed
                  ? { ...theme.custom.glassBox, overflow: "hidden" }
                  : undefined
              }
            >
              <ListItemButton
                aria-current={active ? "page" : undefined}
                aria-expanded={hasChildren ? open : undefined}
                aria-disabled={!ready}
                onClick={() =>
                  hasChildren ? toggle(node.id) : handleNavigate(node)
                }
                sx={{
                  borderRadius: boxed ? 0 : 1,
                  py: 1,
                  pl: 0.5,
                  opacity: ready ? 1 : 0.4,
                  cursor: ready ? "pointer" : "not-allowed",
                  backgroundColor: active ? "#E3F2FD" : "transparent",
                  "&:hover": {
                    backgroundColor: active ? "#D6EAFB" : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                {/* The whole row toggles a group open/closed; this slot just
                    shows the chevron state. Leaves get an empty slot so columns
                    line up. */}
                <ExpandSlot hasChildren={hasChildren} open={open} />
                <ListItemIcon sx={{ minWidth: 38 }}>
                  {Icon && <Icon sx={{ fontSize: 26, color: "#00000080" }} />}
                </ListItemIcon>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 16,
                    fontWeight: active ? 600 : 400,
                    color: "#000000",
                  }}
                >
                  {node.label}
                </Typography>
                <AddBookmarkButton
                  node={node}
                  onAddBookmark={onAddBookmark}
                  bookmarkedIds={bookmarkedIds}
                />
              </ListItemButton>

              {hasChildren && (
                <Collapse in={open} timeout={200} unmountOnExit>
                  <List sx={{ p: 0 }}>
                    {children.map((child) => {
                      const childReady = scopeReady(child, ctx);
                      const childActive = isActive(child);
                      return (
                        <ListItemButton
                          key={child.id}
                          aria-current={childActive ? "page" : undefined}
                          aria-disabled={!childReady}
                          onClick={() => handleNavigate(child)}
                          sx={{
                            borderRadius: boxed ? 0 : 1,
                            py: 1,
                            pl: 0.5,
                            opacity: childReady ? 1 : 0.4,
                            cursor: childReady ? "pointer" : "not-allowed",
                            backgroundColor: childActive
                              ? "#E3F2FD"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: childActive
                                ? "#D6EAFB"
                                : "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {/* Empty expand + icon slots so child labels align
                              under the parent label. */}
                          <ExpandSlot hasChildren={false} />
                          <ListItemIcon sx={{ minWidth: 38 }} />
                          <Typography
                            sx={{
                              flex: 1,
                              fontSize: 16,
                              fontWeight: childActive ? 600 : 400,
                              color: "#000000",
                            }}
                          >
                            {child.label}
                          </Typography>
                          <AddBookmarkButton
                            node={child}
                            onAddBookmark={onAddBookmark}
                            bookmarkedIds={bookmarkedIds}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    </Box>
  );
}
